import React, { useEffect, useRef, useState } from "react";
import Drawer from "rsuite/esm/Drawer";
import Button from "rsuite/esm/Button";
import SelectPicker from "rsuite/esm/SelectPicker";
import Input from "rsuite/esm/Input";
import IconButton from "rsuite/esm/IconButton";
import CheckPicker from "rsuite/esm/CheckPicker";
import DateRangePicker from "rsuite/esm/DateRangePicker";
import Stack from "rsuite/esm/Stack";
import Toggle from "rsuite/esm/Toggle";
import TrashIcon from "@rsuite/icons/Trash";
import PlusIcon from "@rsuite/icons/Plus";
import CloseIcon from "@rsuite/icons/Close";
import ReloadIcon from "@rsuite/icons/Reload";
import { predefinedRanges } from "../../../../utils/utils";
import {
  buildFilterMap,
  getCookie,
  sanitizeFilters,
} from "../../../common/Utility";

const conditionOptions = [
  { label: "contains", value: "contains" },
  { label: "is known", value: "is_known" },
  { label: "not known", value: "not_known" },
  { label: "is equal to", value: "eq" },
  { label: "is not equal to", value: "neq" },
];

const numberConditionOptions = [
  { label: "is greater than", value: "gt" },
  { label: "is less then", value: "lt" },
  { label: "is greater than or equal to", value: "gte" },
  { label: "is less than or equal to", value: "lte" },
  { label: "is known", value: "is_known" },
  { label: "not known", value: "not_known" },
  { label: "is equal to", value: "eq" },
  { label: "is not equal to", value: "neq" },
];

const dateConditionOptions = [
  { label: "is known", value: "is_known" },
  { label: "not known", value: "not_known" },
  { label: "between", value: "between" },
  { label: "not between", value: "not_between" },
];

const errorStyle = {
  border: "1px solid red",
  borderRadius: "6px",
};


const attList = ["select", "api"];

const AdvancedFilterDrawer = ({
  open,
  onClose,
  columnsDef,
  onFilterChange,
  selectedFilter,
  setSelectedFilter,
  fetchFilters,
  editFilter,
  setEditFilter,
  isPublic,
  setIsPublic,
  filters,
  setFilters,
  attribute_set_key,
  filterName,
  setFilterName,
}) => {

  const [apiOptions, setApiOptions] = useState({});
  const [loadingField, setLoadingField] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showInlineSave, setShowInlineSave] = useState(false);
  const [filterErrors, setFilterErrors] = useState({});
  const [openPicker, setOpenPicker] = useState(null);

  let user_uuid = getCookie("muvi_useruuid");

  const saveFilter = async () => {
    const payload = {
      app_token: process.env.REACT_APP_tattle_token,
      attribute_set_key,
      user_uuid:
        user_uuid ||
        "45cfe77780ec435cb7ba63618e013baa",

      filter_name: filterName || "Untitled Filter",
      filter_value: filters,
      is_system: isPublic ? 1 : 0,
      filter_type: 1,
    };

    const apiEndpoint = editFilter
      ? `${process.env.REACT_APP_tattle_api}/update-custom-filter`
      : `${process.env.REACT_APP_tattle_api}/create-custom-filter`;

    if (editFilter) {
      payload.filter_id = editFilter;
      payload.is_default = 0;
    }


    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.code !== 200) {
        throw new Error(data.message || "Failed to save filter");
      }
      const d = await data.data;
      if (!editFilter) {
        const newfilVal = {
          label: d.filter_name || "",
          value: d.filter_id || "",
          filter_value: filters || [],
          type: "custom",
          group: "Custom Filters",
        };
        setSelectedFilter(newfilVal);
      }
      fetchFilters();

      // After successful save:
      // setSelectedFilter
      setFilterName("");
      setShowInlineSave(false);
      setIsPublic(false);
      setEditFilter(null);
      onClose();
    } catch (err) {
      console.error("Save filter failed:", err);
      // Optionally show some error UI to the user
    }
  };

  useEffect(() => {
    if (!editFilter || !selectedFilter) {
      setFilterName("");
      return;
    }

    setFilterName(selectedFilter.label || "");

    if (selectedFilter.filter_value && selectedFilter.filter_value.length > 0) {
      (async () => {
        for (const [index, filter] of selectedFilter.filter_value.entries()) {
          await loadApiOptions(filter.field);
        }
      })();
    }
  }, [editFilter, selectedFilter]);

  useEffect(() => {
    if (!open) {
      // // Reset state when drawer is closed
      setFilterErrors({});
      setErrorMessage("");
      setShowInlineSave(false);
      setFilterName("");
      setIsPublic(false);
    }
    if (open && !editFilter) {
      if (filters && filters.length > 0) {
        (async () => {
          for (const [index, filter] of filters.entries()) {
            await loadApiOptions(filter.field);
          }
        })();
      }
    }
  }, [open]);

  // Validate filters

  useEffect(() => {

    const filterss =
      selectedFilter &&
      Array.isArray(selectedFilter.filter_value) &&
      selectedFilter.filter_value.length > 0
        ? selectedFilter.filter_value
        : [{ field: "", condition: "eq", value: "" }];

    setFilters(filterss);
  }, [selectedFilter]);



  const lastCleanRef = useRef(null);


  const isFirstRun = useRef(true);

  useEffect(() => {
    const runLogic = () => {
      const cleaned = sanitizeFilters(filters);

      const prev = lastCleanRef.current;

      const current = cleaned.map((item) => ({
        ...item,
        value: JSON.stringify(item.value),
      }));

      const hasChanged = JSON.stringify(current) !== JSON.stringify(prev);

      if (hasChanged) {
        lastCleanRef.current = current;
        const result = buildFilterMap(cleaned, columnsDef);
        onFilterChange(result, "advance_filter");
      }
    };

    // ---- FIRST RUN ----
    if (isFirstRun.current) {

      if (columnsDef && columnsDef.length > 1) {
        isFirstRun.current = false;
        runLogic();
      }

      return;
    }

    const timer = setTimeout(runLogic, 800);

    return () => clearTimeout(timer);

  }, [filters, columnsDef]);

  const getFieldConfig = (field) =>
    columnsDef.find((c) => (c.mautic_field || c.field) === field);

  // =============== RESET FIELD VALUE BASED ON TYPE ===============
  const resetValueByType = (attribute_type) => {
    if (attribute_type === "date") return [null, null];
    if (attList.includes(attribute_type)) return [];
    return "";
  };

  // ===================== LOAD API OPTIONS =====================
  const loadApiOptions = async (field) => {
    const fieldConfig = getFieldConfig(field);
    if (!fieldConfig) return;

    try {
      setLoadingField(field);

      if (fieldConfig.attribute_type === "api") {
        const res = await fetch(fieldConfig.api_url);
        let data = await res.json();

        // extract nested data safely
        data = (data && data.data) ? data.data : data;

        // Normalize: convert object → array
        if (!Array.isArray(data)) {
          console.warn("API returned single object, converting to array:", data);
          data = [data];
        }

        const formatted = data.map((item) => ({
          label: item.value,
          value: item.id,
        }));
        setApiOptions(function (prev) {
          return { ...prev, [field]: formatted };
        });
      }

      if (fieldConfig.attribute_type === "select") {
        const formatted = fieldConfig.options.map(function (o) {
          return {
            label: o.option_name,
            value: o.option_value || o.option_name,
          };
        });

        setApiOptions(function (prev) {
          return { ...prev, [field]: formatted };
        });
      }
    } catch (err) {
      console.error("Failed loading API:", err);
    } finally {
      setLoadingField(null);
    }
  };


  const handleChange = async (index, key, value) => {
    const updated = [...filters];
    const fieldConfig = getFieldConfig(updated[index].field);


    // ************ CLEAR ERROR FOR THIS SPECIFIC FIELD ************
    setFilterErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [key]: false, // remove highlight for this field
      },
    }));

    // ---------- FIELD CHANGED ----------
    if (key === "field") {
      const newFieldConfig = getFieldConfig(value);

      updated[index].field = value;

      // Reset value based on new attribute type
      if (newFieldConfig) {
        updated[index].value = resetValueByType(newFieldConfig.attribute_type);

        if (newFieldConfig.attribute_type === "date") {
          updated[index].condition = "between";
        }

        // Load dropdown/api options
        if (
          newFieldConfig.attribute_type === "api" ||
          newFieldConfig.attribute_type === "select"
        ) {
          await loadApiOptions(value);
        }
      }

      setFilters(updated);
      return;
    }

    // ---------- DATE FIELD VALUE CHANGE ----------
    if (
      key === "value" &&
      fieldConfig &&
      fieldConfig.attribute_type === "date"
    ) {
      updated[index].value = value
        ? value.map((d) => (d ? new Date(d).getTime() : null))
        : [null, null];

      setFilters(updated);
      return;
    }

    // ---------- NORMAL VALUE ----------
    updated[index][key] = value;
    setFilters(updated);
  };

  // ----------- ADD CONDITION ------------
  const handleAddCondition = () => {
    let hasAnyError = false;
    let newErrors = {};

    filters.forEach((f, index) => {
      const fieldErr = !f.field;
      const conditionErr = !f.condition;

      const isValueOptional =
        f.condition === "is_known" || f.condition === "not_known";

      const valueErr = isValueOptional
        ? false
        : Array.isArray(f.value)
          ? f.value.length === 0
          : !f.value && f.value !== 0;

      if (fieldErr || conditionErr || valueErr) {
        hasAnyError = true;
        newErrors[index] = {
          field: fieldErr,
          condition: conditionErr,
          value: valueErr
        };
      }
    });

    // If any existing row has error → show highlights and STOP
    if (hasAnyError) {
      setFilterErrors((prev) => ({
        ...prev,
        ...newErrors
      }));
      return;
    }

    // Add new blank row
    setFilters([...filters, { field: "", condition: "eq", value: "" }]);

    // Initialize blank error object for new row
    setFilterErrors((prev) => ({
      ...prev,
      [filters.length]: { field: false, condition: false, value: false },
    }));
  };


  const handleRemoveCondition = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    onClose();
    setEditFilter(null);
  };

  // ===================== RENDER VALUE INPUT =====================
  const renderValueInput = (filter, index) => {

    if (["is_known", "not_known"].includes(filter.condition)) {
      return <React.Fragment></React.Fragment>;
    }

    const fieldConfig = getFieldConfig(filter.field);
    if (!fieldConfig) {
      return <Input placeholder="Select field first" value="" />;
    }

    // ---------- TEXT ----------
    if (
      ["text", "email", "tel", "number", "textarea"].includes(
        fieldConfig.attribute_type,
      )
    ) {
      return (
        <Input
          value={filter.value}
          onChange={(v) => handleChange(index, "value", v)}
          style={{
            ...(filterErrors[index] && filterErrors[index].value
              ? errorStyle
              : {}),
          }}
        />
      );
    }

    if (["toggle"].includes(fieldConfig.attribute_type)) {
      return (
        <SelectPicker
          data={[
            { label: "Yes", value: 1 },
            { label: "No", value: 0 },
          ]}
          value={filter.value}
          placeholder="Select"
          style={{
            width: "100%",
            ...(filterErrors[index] && filterErrors[index].value
              ? errorStyle
              : {}),
          }}
          onChange={(v) => handleChange(index, "value", v)}
          cleanable={false}
          menuClassName={`filter-picker adv-filter-drawer ${openPicker === filter.field ? "dropdown-open" : "dropdown-close"}`}
          onOpen={() => setOpenPicker(filter.field)}
          onClose={() => setOpenPicker(null)}
        />
      );
    }


    if (["select", "api"].includes(fieldConfig.attribute_type)) {
      const field = filter.field;
      const options = apiOptions[field] || [];

      return (
        <CheckPicker
          data={options}
          value={filter.value}
          onChange={(v) => handleChange(index, "value", v)}
          style={{
            width: "100%",
            ...(filterErrors[index] && filterErrors[index].value
              ? errorStyle
              : {}),
          }}
          multiple
          menuClassName={`filter-picker adv-filter-drawer ${openPicker === "value" ? "dropdown-open" : "dropdown-close"} adv-filter-drawer-picker`}
          onOpen={() => setOpenPicker("value")}
          onClose={() => setOpenPicker(null)}
          placement="bottomStart"
        />
      );
    }

    // ---------- DATE ----------
    if (fieldConfig.attribute_type === "date") {
      const uiValue = Array.isArray(filter.value)
        ? filter.value.map((ts) => (ts ? new Date(ts) : null))
        : [null, null];

      return (
        <DateRangePicker
          showOneCalendar
          ranges={predefinedRanges}
          value={uiValue}
          onChange={(v) => handleChange(index, "value", v)}
          style={{ width: "100%" }}
          format="yyyy-MM-dd"
          cleanable
          placement="autoVerticalEnd"
          block
          preventOverflow
          menuClassName={`filter-picker adv-filter-drawer ${openPicker === "value" ? "dropdown-open" : "dropdown-close"}`}
          onOpen={() => setOpenPicker("value")}
        />
      );
    }


    return null;
  };
  const isFilterValid =
    Array.isArray(filters) &&
    filters.some((f) => {
      if (!f || !f.field || !f.condition) return false;

      const isValueOptional =
        f.condition === "is_known" || f.condition === "not_known";

      if (isValueOptional) return true;

      if (Array.isArray(f.value)) {
        return f.value.length > 0;
      }

      return f.value !== "" && f.value !== null;
    });


  return (
    <React.Fragment>
      <Drawer
        open={open}
        onClose={handleApply}
        size="sm"
        placement="right"
        className="advanced-filter-drawer"
      >
        <Drawer.Header>
          <Drawer.Title>Advanced Filters</Drawer.Title>
          <IconButton
            icon={<CloseIcon />}
            appearance="subtle"
            onClick={handleApply}
            style={{
              position: "absolute",
              right: 15,
              top: 15
            }}
          />
        </Drawer.Header>

        <Drawer.Body style={{ padding: "15px 18px" }}>

          {filters.map((filter, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #e5e5e5",
                padding: 6,
                borderRadius: 8,
                marginBottom: 10,
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ gap: "5px" }}
              >
                {columnsDef.length > 0 && (
                  <SelectPicker
                    data={columnsDef
                      .filter((c) => c.attribute_type !== "action")
                      .map((c) => ({
                        label: c.headerName,
                        value: c.mautic_field || c.field,
                      }))}
                    value={filter.field}
                    placeholder="Select field"
                    style={{
                      width: "30%",
                      ...(filterErrors[index] && filterErrors[index].field
                        ? errorStyle
                        : {}),
                    }}
                    onChange={(v) => handleChange(index, "field", v)}
                    menuClassName={`filter-picker adv-filter-drawer ${openPicker === filter.field ? "dropdown-open" : "dropdown-close"}`}
                    onOpen={() => setOpenPicker(filter.field)}
                    onClose={() => setOpenPicker(null)}
                  />
                )}

                <SelectPicker
                  data={
                    getFieldConfig(filter.field) &&
                    getFieldConfig(filter.field).attribute_type === "date"
                      ? dateConditionOptions
                      : getFieldConfig(filter.field) &&
                          getFieldConfig(filter.field).attribute_type ===
                            "number"
                        ? numberConditionOptions
                        : conditionOptions
                  }

                  menuClassName={`filter-picker adv-filter-drawer ${openPicker === filter.field ? "dropdown-open" : "dropdown-close"}`}
                  onOpen={() => setOpenPicker(filter.field)}
                  onClose={() => setOpenPicker(null)}

                  value={filter.condition}
                  onChange={(v) => handleChange(index, "condition", v)}
                  style={{
                    width: "30%",
                    ...(filterErrors[index] && filterErrors[index].condition
                      ? errorStyle
                      : {}),
                  }}
                />

                <div style={{ width: "30%" }}>
                  {renderValueInput(filter, index)}
                </div>

                <IconButton
                  icon={<TrashIcon />}
                  color="red"
                  appearance="ghost"
                  disabled={filters.length === 1}
                  onClick={() => handleRemoveCondition(index)}
                />
              </div>
            </div>
          ))}
          {errorMessage && (
            <div
              style={{
                color: "red",
                marginTop: "-5px",
                marginBottom: "10px",
                fontSize: "13px",
                fontWeight: 500
              }}
            >
              {errorMessage}
            </div>
          )}

          <Button
            appearance="link"
            onClick={handleAddCondition}
            style={{ gap: "3px" }}
          >
            <PlusIcon /> Add Condition
          </Button>
        </Drawer.Body>

        {showInlineSave && (
          <div
            style={{
              position: "absolute",
              bottom: "70px",
              left: "24px",
              background: "#fff",
              padding: "12px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              width: "260px",
              zIndex: 20,
              border: "1px solid #ddd",
            }}
            className="inline-save"
          >
            <p style={{ marginBottom: "6px", fontWeight: 500 }}>
              Save filter as:
            </p>

            <Input
              placeholder="Enter filter name"
              value={filterName}
              onChange={(value) => setFilterName(value)}
              style={{
                marginBottom: "10px",
              }}
            />
            <Stack spacing={10} childrenRenderMode="clone">
              <label>Public: </label>
              <Toggle checked={isPublic} onChange={setIsPublic} size={"sm"} />
            </Stack>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "6px",
              }}
            >
              <Button
                size="xs"
                appearance="subtle"
                onClick={() => {
                  setShowInlineSave(false);
                }}
              >
                Cancel
              </Button>

              <Button
                size="xs"
                appearance="primary"
                onClick={saveFilter}
              >
                {editFilter ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>

        )}


        <div
          className="drawer-footer"
          style={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "10px",
            padding: "12px 15px",
            borderTop: "1px solid #e5e5e5",
            background: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 10
          }}
        >
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowInlineSave(true);
            }}
            disabled={!isFilterValid || showInlineSave}
          >
            {editFilter ? 'Update' : 'Save & Apply'}
          </button>

          <button
            className="btn-dark-btn btn btn-dark"
            onClick={handleApply}
            disabled={!isFilterValid}
          >
            {editFilter ? 'Close' : 'Apply & Close'}
          </button>

          {/* spacer pushes reset icon to right */}
          <div style={{ flex: 1 }} />

          <IconButton
            icon={<ReloadIcon className="flip-180" />}
            appearance="default"
            onClick={() => {
              setFilters([{ field: "", condition: "eq", value: "" }]);
            }}
          />
        </div>


      </Drawer>

    </React.Fragment>
  );
};

export default AdvancedFilterDrawer;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/AdvanceFilterDrawer.js